// Bezug → Eine Bohne, under the bean's figures: what Claude found out about the bean on the web (Dominik 25.10.2026:
// "ja, zeilen und nur anzeigen, bau das board"). Board "Eine Bohne": a card "Claude (i)" like the grinder's, the facts
// as rows behind "Angaben zur Bohne" (the roast with its scale, varieties, the roaster's recipe, tasting notes), the
// sources and "Erneut nachschlagen"; "nicht sicher gefunden" and the sentence before a lookup with a button. Shown only,
// never written into the bean. Without a key nothing shows. Decisions in src/ai/view.ts (beanInfoView).
import { useEffect, useState } from 'react';
import type { BeanItem } from '@brewcore/app-contract';
import { beanInfoView } from '../../ai/view.js';
import { Button, Card, CardHeader, Disclosure, Icon } from '../components/index.js';
import { useMachine } from '../machine-context.js';
import { useAssistant } from './use-assistant.js';

export function BeanInfo({ bean }: { bean: BeanItem }) {
  const { ctx, machineId } = useMachine();
  const { assistant, view } = useAssistant();
  const [open, setOpen] = useState(true);
  const hasKey = !!view.account?.key;
  // the cloud looks a renamed bean (or one of another roaster) up anew: so does this cache
  const asked = `${bean.name}|${bean.roaster ?? ''}`;
  useEffect(() => {
    if (hasKey) void assistant.loadBean(machineId, bean.id, asked);
  }, [assistant, machineId, bean.id, asked, hasKey]);
  const b = beanInfoView(ctx.lang, view, assistant.beanLookup(machineId, bean.id), bean.id);
  if (!b) return null;
  const found = b.status === 'found';
  const lookUp = (again: boolean) => void assistant.lookUpBean(machineId, bean.id, again, ctx.lang);
  return (
    <Card className="tab-card ai-bean" data-testid="bean-claude">
      <CardHeader
        title={
          <span className="ai-bean-title">
            <Icon name="sparkle" />
            {ctx.tr('aiGrinderTitle')}
          </span>
        }
        info={{ text: ctx.tr('aiBeanInfo'), label: ctx.tr('infoShow'), testId: 'bean-claude-help' }}
      />
      {b.text && (
        <p className="ai-text" data-testid="bean-claude-text">
          {b.text}
        </p>
      )}
      {(found || b.busy) && (
        <Disclosure
          open={open && found}
          disabled={!found}
          onClick={() => setOpen(!open)}
          data-testid="bean-claude-open"
        >
          {b.title}
        </Disclosure>
      )}
      {open && found && (
        <>
          <ul className="list ai-bean-rows">
            {b.roast && (
              <li className="list-row" data-testid="bean-claude-roast">
                <span className="row-text">
                  <span className="row-label">{b.roast.label}</span>
                  <span className="hint">{b.roast.value}</span>
                </span>
                {/* the word says it; the scale only shows where, like a taste line on Vivino */}
                <span className="ai-roast" aria-hidden="true">
                  <span className="ai-roast-track">
                    <span className="ai-roast-dot" style={{ left: `${Math.round(b.roast.at * 100)}%` }} />
                  </span>
                  <span className="ai-roast-ends">
                    <span>{b.ends.light}</span>
                    <span>{b.ends.dark}</span>
                  </span>
                </span>
              </li>
            )}
            {b.rows.map((r) => (
              <li key={r.key} className="list-row" data-testid={`bean-claude-${r.key}`}>
                <span className="row-text">
                  <span className="row-label">{r.label}</span>
                  <span className="hint">{r.value}</span>
                </span>
              </li>
            ))}
          </ul>
          {b.sources.length > 0 && (
            <p className="hint ai-sources">
              {ctx.tr('aiSources')}:{' '}
              {b.sources.map((s, i) => (
                <span key={s.href}>
                  {i > 0 && ' · '}
                  <a href={s.href} target="_blank" rel="noopener noreferrer">
                    {s.host}
                  </a>
                </span>
              ))}
            </p>
          )}
        </>
      )}
      {b.action &&
        (found ? (
          <button
            type="button"
            className="link-btn"
            onClick={() => lookUp(true)}
            data-testid="bean-claude-again"
          >
            {b.action.label}
          </button>
        ) : (
          <Button
            variant="secondary"
            block
            icon="search"
            onClick={() => lookUp(b.action?.again ?? false)}
            data-testid="bean-claude-lookup"
          >
            {b.action.label}
          </Button>
        ))}
      {b.error && (
        <p className="field-error" role="alert">
          {b.error}
        </p>
      )}
    </Card>
  );
}
